import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Smartphone, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useMfaFactors, maskPhone } from "@/hooks/use-mfa";
import { MfaEnrollDialog } from "./MfaEnrollDialog";

export function MfaSecurityCard() {
  const { verified, loading, refresh } = useMfaFactors();
  const [enroll, setEnroll] = useState<"totp" | "phone" | null>(null);

  const hasTotp = verified.some((f) => f.factor_type === "totp");
  const hasPhone = verified.some((f) => f.factor_type === "phone");

  const remove = async (id: string) => {
    if (!confirm("Remove this verification method?")) return;
    const { error } = await supabase.auth.mfa.unenroll({ factorId: id });
    if (error) {
      toast.error(error.message.includes("aal2") ? "Please sign out and back in with your code, then try again." : error.message);
      return;
    }
    toast.success("Method removed");
    refresh();
  };

  return (
    <Card id="security" className="p-6">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="h-5 w-5" />
        <h2 className="text-lg font-semibold">Two-step verification</h2>
        {verified.length > 0 ? <Badge>On</Badge> : <Badge variant="secondary">Off</Badge>}
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Add a second step when signing in, using a code from an app or a text message.
      </p>

      {!loading && verified.length > 0 && (
        <ul className="space-y-2 mb-4">
          {verified.map((f) => (
            <li key={f.id} className="flex items-center justify-between rounded border border-border p-3">
              <span className="flex items-center gap-2 text-sm">
                {f.factor_type === "phone" ? <Smartphone className="h-4 w-4" /> : <KeyRound className="h-4 w-4" />}
                {f.factor_type === "phone" ? `Text message to ${maskPhone(f.phone)}` : "Authenticator app"}
              </span>
              <Button variant="ghost" size="sm" onClick={() => remove(f.id)}>Remove</Button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap gap-2">
        {!hasTotp && <Button variant="outline" onClick={() => setEnroll("totp")}><KeyRound className="h-4 w-4 mr-2" />Add authenticator app</Button>}
        {!hasPhone && <Button variant="outline" onClick={() => setEnroll("phone")}><Smartphone className="h-4 w-4 mr-2" />Add text message</Button>}
      </div>

      {enroll && (
        <MfaEnrollDialog open type={enroll} onOpenChange={(o) => !o && setEnroll(null)} onDone={refresh} />
      )}
    </Card>
  );
}
